<i18n src="./translations"></i18n>
<template>
  <div
    v-if="
      hasSignalRow ||
      hasHazardStatements ||
      safetyDocuments.length ||
      (showComplianceDetails &&
        (hasPrecautionaryStatements || isSubjectToBiocideRegulation))
    "
    class="hazardous-good-26"
  >
    <p v-if="showComplianceDetails" class="label">
      {{ $t('hazardous-good-26.label') }}
    </p>
    <div v-if="hasSignalRow" class="signal-row">
      <span class="signal-word">{{
        information.signalWord || $t('hazardous-good-26.signal_word')
      }}</span>
      <div
        v-for="(name, index) in information.icon || []"
        :key="index"
        class="icon"
        :aria-label="$t(`hazardous-good-26.${name.toUpperCase()}`)"
        role="img"
      >
        <Icon :name="upperCamelCase(name)" aria-hidden="true" />
      </div>
    </div>

    <div v-if="hasHazardStatements" class="hazard-block">
      <div class="statements">
        <span class="headline"
          >{{ $t('hazardous-good-26.hazard_warning') }}
        </span>
        <span
          v-for="(statement, index) in information.statements.hazard"
          :key="index"
          class="statement"
        >
          {{ statement.label }}
        </span>
      </div>
    </div>

    <div
      v-if="showComplianceDetails && hasPrecautionaryStatements"
      class="hazard-block"
    >
      <div class="statements">
        <span class="headline">{{
          $t('hazardous-good-26.precautionary_header')
        }}</span>
        <span
          v-for="statement in precautionaryStatements"
          :key="statement.code"
          class="statement"
        >
          {{ statement.label }}
        </span>
      </div>
    </div>

    <p
      v-if="showComplianceDetails && isSubjectToBiocideRegulation"
      class="hazard-block biocide-statement"
    >
      {{ $t('hazardous-good-26.biocide_statement') }}
    </p>

    <p v-if="showComplianceDetails" class="closing-statement">
      {{ $t('hazardous-good-26.closing_statement') }}
    </p>

    <div
      v-for="(doc, index) in safetyDocuments"
      :key="'doc-' + index"
      class="safety-document-wrapper"
    >
      <a
        :href="doc.url"
        class="safety-document-card"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="hazardous-good-safety-document"
      >
        <Icon name="DocumentPdf" class="safety-document-card__icon" />
        <span class="safety-document-card__label">{{ doc.title }}</span>
      </a>
    </div>
  </div>
</template>

<script>
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import Icon from 'Components/00-generated/Icon';

export default {
  name: 'HazardousGood26',
  components: { Icon },
  props: {
    information: {
      type: Object,
      default: () => ({}),
    },
    documents: {
      type: Array,
      default: () => [],
    },
    showComplianceDetails: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasSignalRow() {
      return (
        (this.information.signalWord || '') !== '' ||
        (this.information.icon || []).length !== 0
      );
    },
    hasHazardStatements() {
      return (
        this.information?.statements &&
        this.information.statements.hazard &&
        this.information.statements.hazard.length > 0
      );
    },
    precautionaryStatements() {
      const precautionary = this.information.precautionary || [];

      const statements = Array.isArray(precautionary)
        ? [...precautionary]
        : Object.entries(precautionary).map(([code, label]) => ({
            code,
            label,
          }));

      return statements
        .filter((statement) => statement?.label)
        .sort((a, b) =>
          a.code.localeCompare(b.code, undefined, { numeric: true })
        );
    },
    hasPrecautionaryStatements() {
      return this.precautionaryStatements.length > 0;
    },
    isSubjectToBiocideRegulation() {
      return this.information.isSubjectToBiocideRegulation === true;
    },
    safetyDocuments() {
      return this.documents.filter(
        (doc) => doc && doc.title === 'Sicherheitsdatenblatt'
      );
    },
  },
  methods: {
    upperCamelCase: (string) => upperFirst(camelCase(string)),
  },
};
</script>

<style lang="scss" scoped>
.hazardous-good-26 {
  margin-top: var(--hazardous-good-26-margin-top, var(--space-3));
  margin-bottom: var(--hazardous-good-26-margin-bottom, var(--space-3));

  .label,
  .closing-statement {
    font-size: var(--font-size-S);
    color: var(--color-charcoal-gray);
  }

  .label {
    margin: 0 0 var(--space-1);
  }

  .signal-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--space-1);
    margin-bottom: var(--space-3);
  }

  .signal-word {
    font-weight: bold;
    font-size: var(--font-size-M);
    color: var(--color-nero);
  }

  .icon {
    width: var(--hazardous-good-26-signal-icon-size, var(--space-5));
    height: var(--hazardous-good-26-signal-icon-size, var(--space-5));

    ::v-deep path:nth-of-type(1) {
      fill: var(--color-red);
    }
  }

  .statements {
    font-size: var(--font-size-S);
    color: var(--color-charcoal-gray);
    word-break: break-word;
  }

  .headline {
    font-weight: bold;
  }

  .statement {
    display: inline;

    &:not(:last-child)::after {
      content: ' ';
    }
  }

  .hazard-block + .hazard-block {
    margin-top: var(--space-3);
  }

  .biocide-statement,
  .closing-statement {
    margin-bottom: 0;
  }

  .safety-document-wrapper {
    margin-top: var(--space-3);
  }

  .closing-statement {
    margin-top: var(--space-3);
  }

  .safety-document-card {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1--half);
    padding: var(--space-1--half) var(--space-2);
    border: 1px solid var(--color-concrete);
    border-radius: var(--space-0--half);
    text-decoration: none;
    color: var(--color-nero);

    &:hover {
      border-color: var(--color-secondary);

      .safety-document-card__label {
        text-decoration: underline;
      }
    }

    &__icon {
      flex-shrink: 0;
      width: var(--space-3);
      height: var(--space-3);
    }

    &__label {
      font-size: var(--font-size-M);
      font-weight: bold;
    }
  }
}
</style>
